/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import { useState, useEffect } from 'react';
import { Empty, Spin } from '@arco-design/web-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import request from '@/utils/request';
import API_CONFIG from '@/config/api';
import { EmotionWarningsResponse, EmotionChartData, EmotionWarningItem } from '@/types/emotionAnalysis';
import styles from './EmotionAnalysis.module.less';

// EmotionAnalysis 不再需要 sessionId，直接接收所有 SSE 数据
interface EmotionAnalysisProps {
  // 保留接口以便向后兼容
}

// 格式化时间（只显示时分）
const formatTimeOnly = (dateStr: string): string => {
  try {
    // 支持两种格式："2026-01-18 14:19:11" 或 ISO 格式
    const date = dateStr.includes('T') ? new Date(dateStr) : new Date(dateStr.replace(' ', 'T'));
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  } catch (error) {
    return dateStr;
  }
};

// 根据 score 推断心情（用于 SSE 数据）
const getMoodByScore = (score: number): string => {
  if (score >= 0.8) return '积极';
  if (score >= 0.6) return '平静';
  if (score >= 0.4) return '平淡';
  return '低落';
};

// 格式化图表数据
const formatChartData = (data: EmotionWarningItem[]): EmotionChartData[] => {
  if (!data || data.length === 0) return [];

  // 按时间排序（从旧到新）
  const sorted = [...data].sort((a, b) => 
    new Date(a.created_at.replace(' ', 'T')).getTime() - new Date(b.created_at.replace(' ', 'T')).getTime()
  );

  // 只取最近7天的数据
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  const filtered = sorted.filter(item => 
    new Date(item.created_at.replace(' ', 'T')) >= sevenDaysAgo
  );

  // 转换为图表格式
  return filtered.map(item => {
    const dateStr = item.created_at.replace(' ', 'T');
    const date = new Date(dateStr);
    const time = `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')} ${formatTimeOnly(item.created_at)}`;
    
    return {
      time,
      score: item.score,
      mood: item.current_mood,
      date: item.created_at,
    };
  });
};

// 自定义 Tooltip
function CustomTooltip({ active, payload }: any) {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    
    // 获取情绪颜色和图标（根据实际数据 score 0-1 调整）
    const getMoodColor = (score: number): string => {
      if (score >= 0.8) return '#52c41a';
      if (score >= 0.6) return '#1890ff';
      if (score >= 0.4) return '#faad14';
      return '#ff4d4f';
    };

    const getMoodIcon = (score: number): string => {
      if (score >= 0.8) return '😊';
      if (score >= 0.6) return '🙂';
      if (score >= 0.4) return '😐';
      return '😔';
    };

    return (
      <div style={{
        background: 'rgba(0, 0, 0, 0.9)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '6px',
        padding: '8px 12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
      }}>
        <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '4px' }}>
          {data.date}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '4px' }}>
          <span style={{ fontSize: '16px' }}>{getMoodIcon(data.score)}</span>
          <span>{data.mood}</span>
        </div>
        <div style={{ fontSize: '12px', fontWeight: 600, color: getMoodColor(data.score) }}>
          情绪得分: {data.score.toFixed(2)}
        </div>
      </div>
    );
  }
  return null;
}

function EmotionAnalysis(_props: EmotionAnalysisProps) {
  const [loading, setLoading] = useState(false);
  const [emotionData, setEmotionData] = useState<EmotionWarningItem[]>([]);
  const [chartData, setChartData] = useState<EmotionChartData[]>([]);
  
  // SSE 连接状态（通过全局事件获取）
  const [sseConnected, setSseConnected] = useState(false);
  const [sseMessageCount, setSseMessageCount] = useState(0);

  // 监听 SSE 连接状态变化
  useEffect(() => {
    // 组件挂载时，先读取全局 SSE 连接状态
    // 解决：EmotionWarnings 先建立连接，EmotionAnalysis 后挂载时错过事件的问题
    const currentStatus = (window as any).__sseConnected || false;
    if (currentStatus && !sseConnected) {
      console.log('%c📡 [EmotionAnalysis] 组件挂载，读取到 SSE 已连接', 'color: #52c41a; font-weight: bold');
      setSseConnected(true);
    }

    const handleSseConnectionChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { connected } = customEvent.detail;
      console.log('%c📡 [EmotionAnalysis] SSE 连接状态变化:', 'color: #1890ff; font-weight: bold', connected ? '已连接' : '未连接');
      setSseConnected(connected);
    };

    window.addEventListener('sseConnectionChange', handleSseConnectionChange);

    return () => {
      window.removeEventListener('sseConnectionChange', handleSseConnectionChange);
    };
  }, []);

  // 当收到 SSE 消息时，自动认为已连接
  useEffect(() => {
    if (sseMessageCount > 0 && !sseConnected) {
      console.log('%c📡 [EmotionAnalysis] 收到消息，自动更新连接状态为已连接', 'color: #52c41a; font-weight: bold');
      setSseConnected(true);
    }
  }, [sseMessageCount, sseConnected]);

  // 监听新会话开始事件，清除旧数据
  useEffect(() => {
    const handleNewSessionStarted = (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #faad14');
      console.log('%c🔄 [EmotionAnalysis] 新会话开始，清除旧数据', 'color: #faad14; font-weight: bold; font-size: 13px');
      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #faad14');
      console.log('新 session_id:', customEvent.detail?.sessionId);
      console.log('时间:', new Date().toLocaleTimeString());
      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #faad14');
      
      // 清除所有状态数据
      setEmotionData([]);
      setChartData([]);
      setSseMessageCount(0);
      setSseConnected(false);
      
      console.log('%c✅ [EmotionAnalysis] 情绪数据已清除，等待新会话数据...', 'color: #52c41a; font-weight: bold');
    };

    window.addEventListener('newSessionStarted', handleNewSessionStarted);

    return () => {
      window.removeEventListener('newSessionStarted', handleNewSessionStarted);
    };
  }, []);

  // 处理 SSE 实时数据更新 - 直接接收所有数据，不再依赖 sessionId
  useEffect(() => {
    const handleEmotionDataUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      const rawData = customEvent.detail;
      
      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #13c2c2');
      console.log('%c📊 [EmotionAnalysis] 收到 SSE 情绪数据', 'color: #13c2c2; font-weight: bold; font-size: 13px');
      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #13c2c2');
      console.log('原始数据:', rawData);
      
      // 递增 SSE 消息计数
      setSseMessageCount((prev) => prev + 1);
      
      // SSE 数据结构: { session_id, message_type, data: { score, current_mood, ... } }
      // 提取实际数据（可能在 data 字段中，也可能直接在顶层）
      const emotionInfo = rawData.data || rawData;
      
      console.log('提取的情绪信息:', emotionInfo);
      console.log('score:', emotionInfo.score);
      console.log('current_mood:', emotionInfo.current_mood);
      
      // 使用 SSE 返回的时间戳，如果没有则生成当前时间
      const timestamp = emotionInfo.created_at || emotionInfo.updated_at || (() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
      })();
      
      // 构造新的情绪数据项
      const newEmotionItem: EmotionWarningItem = {
        id: emotionInfo.id || Date.now(),
        session_id: emotionInfo.session_id || rawData.session_id || '',
        username: emotionInfo.username || '',
        current_mood: emotionInfo.current_mood || getMoodByScore(emotionInfo.score || 0),
        warning_type: emotionInfo.warning_type || '无',
        score: typeof emotionInfo.score === 'number' ? emotionInfo.score : parseFloat(emotionInfo.score) || 0,
        reasons: emotionInfo.reasons || [],
        related_chats: emotionInfo.related_chats || [],
        suggestions: emotionInfo.suggestions || [],
        created_at: timestamp,
        updated_at: emotionInfo.updated_at || timestamp,
      };
      
      console.log('✅ 添加到情绪趋势数据:', newEmotionItem);
      console.log('得分:', newEmotionItem.score, '心情:', newEmotionItem.current_mood);
      
      setEmotionData((prev) => {
        const updated = [...prev, newEmotionItem];
        console.log(`情绪数据: ${prev.length} -> ${updated.length} 条`);
        
        // 更新图表数据
        const newChartData = formatChartData(updated);
        setChartData(newChartData);
        
        return updated;
      });
      
      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #13c2c2');
    };

    window.addEventListener('emotionDataUpdate', handleEmotionDataUpdate);

    return () => {
      window.removeEventListener('emotionDataUpdate', handleEmotionDataUpdate);
    };
  }, []);

  // 加载历史情绪数据（可选，基于 API）
  const loadEmotionData = async () => {
    setLoading(true);
    try {
      const response = await request.get<EmotionWarningsResponse>(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.EMOTION_WARNINGS_LIST}`,
        {
          params: {
            page: 1,
            page_size: 50,
          },
        }
      );

      if (response.code === 200 && response.data) {
        console.log('✅ 历史情绪数据加载成功:', response.data);
        setEmotionData(response.data);
        
        // 转换为图表数据
        const formatted = formatChartData(response.data);
        setChartData(formatted);
      }
    } catch (error: any) {
      console.warn('⚠️ 加载历史情绪数据失败（可能是首次对话）:', error.message);
      // 不显示错误提示，等待 SSE 数据
    } finally {
      setLoading(false);
    }
  };

  // 组件挂载时尝试加载历史数据
  useEffect(() => {
    loadEmotionData();
  }, []);

  // 获取情绪颜色（根据实际数据 score 0-1 调整）
  const getMoodColor = (score: number): string => {
    if (score >= 0.8) return '#52c41a'; // 绿色 - 良好
    if (score >= 0.6) return '#1890ff'; // 蓝色 - 正常
    if (score >= 0.4) return '#faad14'; // 黄色 - 需关注
    return '#ff4d4f'; // 红色 - 需警惕
  };

  // 获取情绪图标（根据实际数据 score 0-1 调整）
  const getMoodIcon = (score: number): string => {
    if (score >= 0.8) return '😊';
    if (score >= 0.6) return '🙂';
    if (score >= 0.4) return '😐';
    return '😔';
  };

  // 渲染加载状态
  if (loading) {
    return (
      <div className={styles.emotionAnalysis}>
        <div className={styles.header}>
          <div className={styles.title}>情绪分析</div>
        </div>
        <div className={styles.loadingState}>
          <Spin />
          <div className={styles.loadingText}>加载中...</div>
        </div>
      </div>
    );
  }

  // 渲染等待数据状态
  if (emotionData.length === 0) {
    return (
      <div className={styles.emotionAnalysis}>
        <div className={styles.header}>
          <div className={styles.title}>情绪分析</div>
          <div className={styles.headerRight}>
            <div className={styles.sseStatus}>
              <span className={`${styles.statusDot} ${sseConnected ? styles.connected : styles.disconnected}`} />
              <span className={styles.statusText}>{sseConnected ? '已连接' : '未连接'}</span>
            </div>
          </div>
        </div>
        <div className={styles.emptyState}>
          <Empty description={sseConnected ? '等待情绪数据...' : '等待连接...'} />
        </div>
      </div>
    );
  }

  // 获取最新的情绪数据
  const latestEmotion = emotionData[emotionData.length - 1];

  return (
    <div className={styles.emotionAnalysis}>
      <div className={styles.header}>
        <div className={styles.title}>情绪分析</div>
        <div className={styles.headerRight}>
          <div className={styles.sseStatus}>
            <span className={`${styles.statusDot} ${sseConnected ? styles.connected : styles.disconnected}`} />
            <span className={styles.statusText}>{sseConnected ? '已连接' : '未连接'}</span>
          </div>
          <div className={styles.messageCount}>
            推送: {sseMessageCount} 条
          </div>
        </div>
      </div>

      {/* 当前情绪状态 */}
      <div className={styles.currentMood}>
        <div className={styles.moodIcon}>{getMoodIcon(latestEmotion.score)}</div>
        <div className={styles.moodInfo}>
          <div className={styles.moodName}>{latestEmotion.current_mood}</div>
          <div className={styles.moodMeta}>
            <div 
              className={styles.moodScore}
              style={{ color: getMoodColor(latestEmotion.score) }}
            >
              得分: {latestEmotion.score.toFixed(2)}
            </div>
            {latestEmotion.warning_type && latestEmotion.warning_type !== '无' && (
              <div className={styles.warningType}>
                预警: {latestEmotion.warning_type}
              </div>
            )}
          </div>
          <div className={styles.updateTime}>
            更新时间: {formatTimeOnly(latestEmotion.updated_at)}
          </div>
        </div>
      </div>

      {/* 情绪曲线图 */}
      <div className={styles.chartContainer}>
        <div className={styles.chartTitle}>情绪趋势（最近对话）</div>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
            <XAxis 
              dataKey="time" 
              stroke="rgba(255, 255, 255, 0.5)"
              tick={{ fill: 'rgba(255, 255, 255, 0.7)', fontSize: 11 }}
            />
            <YAxis 
              domain={[0, 1]}
              ticks={[0, 0.2, 0.4, 0.6, 0.8, 1]}
              stroke="rgba(255, 255, 255, 0.5)"
              tick={{ fill: 'rgba(255, 255, 255, 0.7)', fontSize: 11 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={0.5} stroke="rgba(255, 255, 255, 0.3)" strokeDasharray="3 3" label={{ value: '平均线', fill: 'rgba(255, 255, 255, 0.5)', fontSize: 10 }} />
            <Line 
              type="monotone" 
              dataKey="score" 
              stroke="#1890ff" 
              strokeWidth={1}
              dot={{ fill: '#1890ff', r: 2 }}
              activeDot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* 相关对话 - 只有从 API 获取的数据才有，SSE 数据没有 */}
      {latestEmotion.related_chats && latestEmotion.related_chats.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>💬 相关对话</div>
          <div className={styles.chatList}>
            {latestEmotion.related_chats.map((chat, index) => (
              <div key={index} className={styles.chatItem}>
                {chat}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 情绪原因 - 只有从 API 获取的数据才有，SSE 数据没有 */}
      {latestEmotion.reasons && latestEmotion.reasons.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>📋 情绪原因</div>
          <ul className={styles.list}>
            {latestEmotion.reasons.map((reason, index) => (
              <li key={index} className={styles.listItem}>{reason}</li>
            ))}
          </ul>
        </div>
      )}

      {/* 改善建议 - 只有从 API 获取的数据才有，SSE 数据没有 */}
      {latestEmotion.suggestions && latestEmotion.suggestions.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>💡 改善建议</div>
          <ul className={styles.list}>
            {latestEmotion.suggestions.map((suggestion, index) => (
              <li key={index} className={styles.listItem}>{suggestion}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default EmotionAnalysis;
